import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateOrderStatus } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChefHat, Clock } from 'lucide-react';
import { toast } from 'sonner';
import wsService from '@/services/websocket';
import apiClient from '@/services/api';

const KitchenApp = () => {
  const [user, setUser] = useState(null);
  const [activeOrders, setActiveOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [cancelledOrders, setCancelledOrders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData.id || !userData.store_id) {
      navigate('/login');
      return;
    }
    setUser(userData);
    loadOrders(userData.store_id);

    // WebSocket with reconnection handling
    wsService.connect(`kitchen:store:${userData.store_id}`);
    
    wsService.on('order.created', (data) => {
      console.log('KDS: Received order.created', data);
      loadOrders(userData.store_id);
    });
    
    wsService.on('order.updated', (data) => {
      console.log('KDS: Received order.updated', data);
      loadOrders(userData.store_id);
    });
    
    wsService.on('order.cancelled', (data) => {
      console.log('KDS: Order cancelled', data);
      toast.info(`Order #${data.order_number} was cancelled`);
      loadOrders(userData.store_id);
    });

    // Listen for day-end event
    wsService.on('day.end', (data) => {
      console.log('KDS: Day ended - clearing all orders');
      toast.info('Day has ended - All orders cleared', { duration: 5000 });
      // Clear all orders from display
      setActiveOrders([]);
      setCompletedOrders([]);
      setCancelledOrders([]);
    });

    // On reconnection, reload orders
    wsService.onConnect(() => {
      console.log('KDS: WebSocket connected - reloading orders');
      loadOrders(userData.store_id);
    });

    const interval = setInterval(() => loadOrders(userData.store_id), 10000);
    
    return () => {
      wsService.disconnect();
      clearInterval(interval);
    };
  }, []);

  const loadOrders = async (storeId) => {
    try {
      // CRITICAL: Use kitchen=true to get SANITIZED data (NO PII)
      const response = await apiClient.get(`/orders?store_id=${storeId}&kitchen=true&limit=100`);
      const orders = response.data;
      
      // Filter active orders (NEW, PREPARING)
      const active = orders.filter(o => ['NEW', 'PREPARING'].includes(o.status));
      
      // Filter ready orders for completed tab
      const ready = orders.filter(o => 
        ['READY_DINEIN', 'READY_TAKEAWAY', 'READY_DELIVERY'].includes(o.status)
      );
      
      // Filter cancelled orders
      const cancelled = orders.filter(o => o.status === 'CANCELLED');
      
      setActiveOrders(active);
      setCompletedOrders(ready.slice(0, 20));
      setCancelledOrders(cancelled.slice(0, 20));
      
      console.log(`KDS: Loaded ${active.length} active, ${cancelled.length} cancelled orders (SANITIZED - NO PII)`);
    } catch (error) {
      console.error('KDS: Error loading orders:', error);
      toast.error('Failed to load orders');
    }
  };

  const handleStatusUpdate = async (orderId, newStatus, orderType) => {
    try {
      // When marking READY, backend will automatically set correct status based on order type
      // DINE_IN -> READY_DINEIN, TAKEAWAY -> READY_TAKEAWAY, DELIVERY -> READY_DELIVERY
      await updateOrderStatus(orderId, newStatus);
      toast.success('Order status updated');
      loadOrders(user.store_id);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleToggleItemPrepared = async (orderId, itemIndex, nextStatus) => {
    // Kitchen-internal only - never touches the order's overall status,
    // so this can't ever affect what a customer sees in their own tracking.
    try {
      await apiClient.put(`/orders/${orderId}/item-prepared?item_index=${itemIndex}&prep_status=${nextStatus}`);
      loadOrders(user.store_id);
    } catch (error) {
      console.error('Error updating item status:', error);
      toast.error('Failed to update item');
    }
  };

  const nextPrepStatus = (current) => {
    // Stops at PREPARED rather than cycling back to PENDING - an item
    // already marked prepared genuinely has ingredients/effort committed,
    // and looping back would incorrectly unlock it for deletion in the
    // Post Pay edit screen (which treats PENDING as "safe to remove").
    // Undoing a mistake here should be a deliberate action, not something
    // a stray extra tap can trigger.
    const cycle = { PENDING: 'PREPARING', PREPARING: 'PREPARED', PREPARED: 'PREPARED' };
    return cycle[current || 'PENDING'] || 'PREPARING';
  };

  const getTimeSince = (createdAt) => {
    const diff = Math.floor((new Date() - new Date(createdAt)) / 1000 / 60);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    const hours = Math.floor(diff / 60);
    return `${hours}h ${diff % 60}m ago`;
  };

  const getOrderTypeColor = (type) => {
    const colors = {
      DINE_IN: 'bg-blue-600',
      TAKEAWAY: 'bg-green-600',
      DELIVERY: 'bg-purple-600'
    };
    return colors[type] || 'bg-gray-600';
  };

  const OrderCard = ({ order }) => (
    <Card data-testid={`order-${order.order_number}`} className="bg-gray-800 border-gray-700">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-white">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-2xl font-bold">#{order.order_number}</span>
            <span className={`text-sm px-3 py-1 rounded ${getOrderTypeColor(order.order_type)}`}>
              {order.order_type}
            </span>
            {order.is_post_pay && (
              <span className="text-xs px-2 py-1 rounded bg-amber-500 text-white font-bold" data-testid={`post-pay-${order.order_number}`}>
                POST PAY - more items may follow
              </span>
            )}
            {order.tent_card_number && (
              <span className="text-xs px-2 py-1 rounded bg-slate-600 text-white font-semibold" data-testid={`tent-card-${order.order_number}`}>
                Tent #{order.tent_card_number}
              </span>
            )}
          </div>
          <div className="flex items-center text-sm text-gray-400">
            <Clock className="w-4 h-4 mr-1" />
            {getTimeSince(order.created_at)}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* NO CUSTOMER PII - DEFENSIVE: Should already be stripped by backend */}
        {order.customer && (
          <div className="text-sm text-red-500 mb-2">
            ERROR: Customer PII detected in KDS! Contact admin immediately.
          </div>
        )}
        
        <div className="space-y-2 mb-4">
          {order.items.map((item, idx) => {
            const status = item.prep_status || 'PENDING';
            const isPreparing = status === 'PREPARING';
            const isPrepared = status === 'PREPARED';
            const removed = item.edit_state === 'REMOVED';
            const added = item.edit_state === 'ADDED';
            return (
            <div
              key={idx}
              className={`p-2 rounded flex items-start gap-2 transition-colors ${
                removed ? 'bg-red-950/30 border border-red-800 opacity-60 cursor-not-allowed' :
                isPrepared ? 'bg-green-900/30 border border-green-700 cursor-pointer' :
                isPreparing ? 'bg-amber-900/30 border border-amber-600 cursor-pointer' :
                'bg-gray-900 cursor-pointer'
              }`}
              onClick={() => !removed && handleToggleItemPrepared(order.id, idx, nextPrepStatus(status))}
              data-testid={`item-prepared-toggle-${order.order_number}-${idx}`}
            >
              <div className={`mt-0.5 w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center ${
                isPrepared ? 'bg-green-500 border-green-500' :
                isPreparing ? 'bg-amber-500 border-amber-500' :
                'border-gray-500'
              }`}>
                {isPrepared && <span className="text-white text-xs font-bold">✓</span>}
                {isPreparing && <span className="text-white text-xs font-bold">•</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`flex justify-between ${isPrepared ? 'text-green-300 line-through' : removed ? 'text-red-400 line-through' : isPreparing ? 'text-amber-300' : 'text-white'}`}>
                  <span className="font-semibold">
                    {item.qty}x {item.menu_item_name}
                    {added && <span className="ml-2 text-xs font-bold text-green-400">NEW</span>}
                    {removed && <span className="ml-2 text-xs font-bold text-red-400">REMOVED</span>}
                  </span>
                </div>
                {(item.variant || (item.modifiers && item.modifiers.length > 0)) && (
                  <div className="text-xs text-blue-300 mt-1">
                    {item.variant && <span className="mr-2">{typeof item.variant === 'string' ? item.variant : item.variant.name}</span>}
                    {(item.modifiers || []).map((mod, midx) => (
                      <span key={midx} className="mr-2">+{typeof mod === 'string' ? mod : mod.name}</span>
                    ))}
                  </div>
                )}
                {item.notes && (
                  <div className="text-xs text-yellow-400 mt-1">Note: {item.notes}</div>
                )}
              </div>
            </div>
            );
          })}
        </div>
        
        {order.special_instructions && (
          <div className="bg-yellow-900/30 border border-yellow-600 p-2 rounded mb-3">
            <div className="text-xs text-yellow-400 font-semibold">Special Instructions:</div>
            <div className="text-sm text-yellow-200">{order.special_instructions}</div>
          </div>
        )}

        <div className="flex gap-2">
          {order.status === 'NEW' && (
            <Button 
              data-testid="btn-start-preparing"
              className="flex-1 bg-yellow-600 hover:bg-yellow-700"
              onClick={() => handleStatusUpdate(order.id, 'PREPARING', order.order_type)}
            >
              Start Preparing
            </Button>
          )}
          {order.status === 'PREPARING' && (
            <Button 
              data-testid="btn-mark-ready"
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={() => handleStatusUpdate(order.id, 'READY', order.order_type)}
            >
              Mark Ready
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const CancelledOrderCard = ({ order }) => (
    <Card data-testid={`order-${order.order_number}`} className="bg-red-900/20 border-2 border-red-600">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-red-400">
          <div>
            <span className="text-2xl font-bold text-red-500">#{order.order_number}</span>
            <span className={`ml-3 text-sm px-3 py-1 rounded bg-red-600 text-white`}>
              {order.order_type}
            </span>
            <span className="ml-2 px-2 py-1 text-xs font-bold bg-red-700 text-white rounded">
              CANCELLED
            </span>
          </div>
          <div className="flex items-center text-sm text-red-400">
            <Clock className="w-4 h-4 mr-1" />
            {getTimeSince(order.created_at)}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 mb-4">
          {order.items.map((item, idx) => (
            <div key={idx} className="bg-red-950/40 p-2 rounded border border-red-800">
              <div className="flex justify-between text-red-300">
                <span className="font-semibold">{item.qty}x {item.menu_item_name}</span>
              </div>
              {(item.variant || (item.modifiers && item.modifiers.length > 0)) && (
                <div className="text-xs text-red-200 mt-1">
                  {item.variant && <span className="mr-2">{typeof item.variant === 'string' ? item.variant : item.variant.name}</span>}
                  {(item.modifiers || []).map((mod, midx) => (
                    <span key={midx} className="mr-2">+{typeof mod === 'string' ? mod : mod.name}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        
        {order.cancellation && order.cancellation.cancel_reason && (
          <div className="bg-red-900/50 border border-red-600 p-2 rounded mb-3">
            <div className="text-xs text-red-300 font-semibold">Cancellation Reason:</div>
            <div className="text-sm text-red-200">{order.cancellation.cancel_reason}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );


  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="bg-gray-800 shadow-md border-b border-gray-700 sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <ChefHat className="w-6 h-6 text-orange-500 mr-2" />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold">Kitchen Display</h1>
                <span className="text-xs text-red-600 font-medium">(Designed by Prashant B)</span>
              </div>
              <p className="text-xs text-gray-400">{user?.name}</p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/login')}>Logout</Button>
        </div>
      </div>

      <Tabs defaultValue="active" className="px-4 mt-4">
        <TabsList className="grid w-full grid-cols-3 bg-gray-800">
          <TabsTrigger value="active" className="data-[state=active]:bg-orange-600">
            Active Orders ({activeOrders.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-green-600">
            Ready ({completedOrders.length})
          </TabsTrigger>
          <TabsTrigger value="cancelled" className="data-[state=active]:bg-red-600">
            Cancelled ({cancelledOrders.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4">
          {activeOrders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ChefHat className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No active orders</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeOrders.map((order) => (
                <OrderCard key={order.id} order={order} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="mt-4">
          {completedOrders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ChefHat className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No ready orders</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {completedOrders.map((order) => (
                <OrderCard key={order.id} order={order} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="cancelled" className="mt-4">
          {cancelledOrders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ChefHat className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No cancelled orders</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cancelledOrders.map((order) => (
                <CancelledOrderCard key={order.id} order={order} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default KitchenApp;
